import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { acars, dispatch } from "@/lib/api";
import { useElectronSim, isDesktop, useAppVersion } from "@/lib/electron";
import { LogOut, LayoutDashboard, Radio, BookOpen, Settings, PlaneTakeoff, Map, ClipboardList, Music2, MessageSquare, FlaskConical, Sparkles, LifeBuoy, ExternalLink, UserPlus } from "lucide-react";
import Brand from "@/components/Brand";
import VolumeControl from "@/components/VolumeControl";
import ThemeToggle from "@/components/ThemeToggle";
import FlightRecorder from "@/components/FlightRecorder";
import { useUnseenRelease } from "@/lib/changelog";
import { PILOT_GUIDE_URL } from "@/lib/links";

const NAV_BASE = [
  { to: "/",         icon: LayoutDashboard, label: "Dashboard",       testid: "nav-dashboard" },
  { to: "/fpls",     icon: ClipboardList,   label: "Flight Plan",     testid: "nav-fpls" },
  { to: "/init",     icon: PlaneTakeoff,    label: "Init Flight",     testid: "nav-init" },
  { to: "/tracking", icon: Radio,           label: "Live Tracking",   testid: "nav-tracking" },
  { to: "/livemap",  icon: Map,             label: "Live Map",        testid: "nav-livemap" },
  { to: "/dispatch", icon: MessageSquare,   label: "Dispatcher",      testid: "nav-dispatch", badgeKey: "dispatch" },
  { to: "/audio",    icon: Music2,          label: "Audio",           testid: "nav-audio" },
  { to: "/logbook",  icon: BookOpen,        label: "Logbook",         testid: "nav-logbook" },
  { to: "/settings", icon: Settings,        label: "Settings",        testid: "nav-settings" },
  // Only for pilots in BETA_PILOTS (and admins); the server sets user.is_beta.
  { to: "/beta",     icon: FlaskConical,    label: "Beta test",       testid: "nav-beta", betaOnly: true },
];

const NAV_ADMIN = [
  { to: "/admin/sounds", icon: Music2, label: "Soundpacks (admin)", testid: "nav-admin-sounds" },
  { to: "/admin/beta",   icon: FlaskConical, label: "Beta reports",  testid: "nav-admin-beta" },
  { to: "/admin/beta-applications", icon: UserPlus, label: "Beta applications", testid: "nav-admin-beta-apps" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();

  const { data: active } = useQuery({
    queryKey: ["acars-active"],
    queryFn: acars.active,
    refetchInterval: 8000,
    // React Query pauses interval polling while the page is hidden, and
    // Chromium calls the desktop window hidden whenever another window covers
    // it: in flight, the sim, for the whole flight. Polling stopped the moment
    // the pilot switched to the sim, so a gate found later never reached the
    // in-sim overlay (useElectronSim below). In a browser tab, pausing is right.
    refetchIntervalInBackground: isDesktop(),
    enabled: !!user,
  });

  const { data: unread } = useQuery({
    queryKey: ["dispatch-unread"],
    queryFn: dispatch.unread,
    refetchInterval: 20000,
    enabled: !!user,
  });

  // Feed the Electron shell (Discord Rich Presence + telemetry poster) with
  // the current active flight, so RPC keeps updating no matter which page
  // the pilot is looking at. In the browser this is a no-op.
  const sim = useElectronSim(active);
  const appVersion = useAppVersion();
  const unseenRelease = useUnseenRelease();

  const doLogout = () => {
    logout();
    nav_("/login");
  };

  return (
    // h-screen, not min-h-screen. min-h- is a floor, so the flex row grew to
    // the tallest child and the stretched sidebar's footer — status pill,
    // volume, logout — sat at the bottom of the *document* rather than the
    // viewport. On a long page (Settings, Live Tracking) that is off screen.
    // Pinning the shell to the viewport also makes <main>'s overflow-y-auto
    // do something: it had no height to overflow before, so the document
    // scrolled instead of the panel.
    <div className="h-screen overflow-hidden flex text-foreground">
      {/* Here, not in a page, so a clip keeps playing when the pilot navigates. */}
      <FlightRecorder onGround={sim.telemetry?.on_ground} />
      {/* Sidebar */}
      <aside className="app-sidebar w-64 shrink-0 border-r border-border flex flex-col h-full" data-testid="app-sidebar">
        {/* Brand: the VAC roundel and the VACARS5 title, as on the dispatcher
            dashboard. Text rather than a lettered image, so it suits both themes. */}
        <div className="shrink-0 px-5 py-5 border-b border-border" data-testid="sidebar-brand">
          <Brand />
        </div>

        {/* Nav */}
        {/* min-h-0 is what lets this actually scroll: a flex item's default
            min-height is auto, so without it the nav refuses to shrink below
            its content and pushes the footer out of the sidebar again. */}
        <nav className="flex-1 min-h-0 py-4 px-2 space-y-1 overflow-y-auto">
          {NAV_BASE.filter((n) => !n.betaOnly || user?.is_beta).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "nav-item-active"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
              {n.badgeKey === "dispatch" && unread?.count > 0 && (
                <span
                  data-testid="nav-dispatch-badge"
                  className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
                >
                  {unread.count}
                </span>
              )}
            </NavLink>
          ))}
          {/* Help: the release notes and the guide, from every page. */}
          <div className="mt-4 px-3 pt-2 overline border-t border-border" data-testid="nav-help-header">Help</div>
          <NavLink
            to="/whats-new"
            data-testid="nav-whats-new"
            className={({ isActive }) =>
              `nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "nav-item-active"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
          >
            <Sparkles className="w-4 h-4" />
            <span className="flex-1">What's new</span>
            {unseenRelease && (
              <span data-testid="nav-whats-new-dot" title="A new release you haven't read about"
                className="w-2 h-2 rounded-full bg-primary" />
            )}
          </NavLink>
          <a
            href={PILOT_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-guide"
            className="nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <LifeBuoy className="w-4 h-4" />
            <span className="flex-1">Pilot guide</span>
            <ExternalLink className="w-3 h-3" />
          </a>
          {user?.is_admin && (
            <>
              <div className="mt-4 px-3 pt-2 overline border-t border-border" data-testid="nav-admin-header">Admin</div>
              {NAV_ADMIN.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? "nav-item-active"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  <span className="flex-1">{n.label}</span>
                  {n.badgeKey === "dispatch" && unread?.count > 0 && (
                    <span
                      data-testid="nav-dispatch-badge"
                      className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
                    >
                      {unread.count}
                    </span>
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* Active flight status pill (bottom of sidebar) */}
        <div className="shrink-0 border-t border-border p-3 space-y-3">
          {active ? (
            <Link
              to="/tracking"
              data-testid="header-active-flight"
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-primary bg-primary/10 text-primary text-xs font-mono uppercase tracking-widest hover:bg-primary/20 transition-colors"
            >
              <span className="live-dot"></span>
              {active.orig}→{active.dest} · {active.flt}
            </Link>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid="header-idle">
              <span className="live-dot dead-dot"></span>
              No active flight
            </div>
          )}

          <VolumeControl />

          <ThemeToggle />

          <div className="flex items-center gap-2 px-2">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-xs font-mono font-bold">
              {user?.pid?.slice(-3) || "—"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-xs text-muted-foreground font-mono truncate">{user?.pid}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="btn-logout"
              className="p-1.5 text-muted-foreground hover:text-primary transition-colors rounded-md"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {appVersion && (
            <Link to="/whats-new" title="What's new in this version"
              className="block px-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="app-version">
              VACARS5 v{appVersion}
            </Link>
          )}
        </div>
      </aside>

      {/* Main – no top header */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
